import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';
import { Progress } from '../../ui/progress';

export interface StepShellProps {
  title: string;
  /** Optional step subtitle. Omit when a `brand` block drives the header instead. */
  subtitle?: string;
  /** Optional brand block rendered ABOVE the title (e.g. company logo + job name). */
  brand?: ReactNode;
  /** 0-100 progress percentage */
  progress: number;
  children: ReactNode;
  /** Actions row (e.g. back/next buttons) */
  footer: ReactNode;
  /** Max container width (default: 480px, md: 704px) */
  maxWidth?: string;
  className?: string;
}

function StepShell({
  title,
  subtitle,
  brand,
  progress,
  children,
  footer,
  maxWidth,
  className,
}: StepShellProps) {
  return (
    <div
      data-slot="step-shell"
      className={cn(
        'relative mx-auto flex min-h-dvh w-full max-w-[480px] flex-col overflow-hidden bg-background md:max-w-[704px]',
        className
      )}
      style={maxWidth ? { maxWidth } : undefined}
    >
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 py-12 md:px-8 md:py-14">
        <header className="flex shrink-0 flex-col gap-1.5">
          {brand}
          <h1 className="text-2xl font-semibold leading-none tracking-tight text-foreground">
            {title}
          </h1>
          {subtitle ? (
            <p className="text-lg font-medium leading-none text-accent-foreground">{subtitle}</p>
          ) : null}
        </header>
        <Progress value={progress} />
        <div className="flex flex-1 flex-col gap-6">{children}</div>
        {footer}
      </div>
    </div>
  );
}
StepShell.displayName = 'StepShell';

export { StepShell };
